import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, AlertCircle } from "lucide-react";
import { fmtDate } from "@/lib/format";

const STATUSES = [
  { v: "present", label: "Present", icon: CheckCircle2, color: "text-emerald-400" },
  { v: "late", label: "Late", icon: Clock, color: "text-amber-400" },
  { v: "excused", label: "Excused", icon: AlertCircle, color: "text-blue-400" },
  { v: "absent", label: "Absent", icon: XCircle, color: "text-red-400" },
];

export default function Attendance({ embedded = false }) {
  const [rehearsals, setRehearsals] = useState([]);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState("");
  const [attendance, setAttendance] = useState([]);

  const load = async () => {
    const [r, u] = await Promise.all([api.get("/rehearsals"), api.get("/users")]);
    setRehearsals(r.data);
    setUsers(u.data.filter((x) => x.role === "cast"));
    if (r.data.length && !selected) setSelected(r.data[0].id);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selected) return;
    api.get("/attendance", { params: { rehearsal_id: selected } }).then((r) => setAttendance(r.data));
  }, [selected]);

  const map = useMemo(() => Object.fromEntries(attendance.map((a) => [a.user_id, a])), [attendance]);

  const setStatus = async (userId, status) => {
    await api.post("/attendance", { rehearsal_id: selected, user_id: userId, status, notes: "" });
    const r = await api.get("/attendance", { params: { rehearsal_id: selected } });
    setAttendance(r.data);
    toast.success("Attendance saved");
  };

  return (
    <div className={embedded ? "space-y-4" : "space-y-6 animate-fade-up"} data-testid="attendance-page">
      {!embedded && (
        <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Roll call</div>
            <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Attendance</h1>
            <p className="mt-1 text-zinc-400 text-sm">Track presence and absences per rehearsal.</p>
          </div>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="bg-[#121212] border-white/10 md:w-96"><SelectValue placeholder="Choose rehearsal" /></SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 text-white max-h-80">
              {rehearsals.map((r) => (
                <SelectItem key={r.id} value={r.id}>{fmtDate(r.date)} · {r.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </header>
      )}
      {embedded && (
        <div className="flex justify-end">
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="bg-[#121212] border-white/10 md:w-96"><SelectValue placeholder="Choose rehearsal" /></SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 text-white max-h-80">
              {rehearsals.map((r) => (
                <SelectItem key={r.id} value={r.id}>{fmtDate(r.date)} · {r.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="rounded-lg border border-white/10 bg-[#121212] divide-y divide-white/5">
        {users.length === 0 && <div className="p-8 text-sm text-zinc-500">No cast members yet.</div>}
        {users.map((u) => {
          const cur = map[u.id]?.status;
          return (
            <div key={u.id} className="p-4 flex items-center gap-4">
              <div className="w-9 h-9 rounded-full bg-[#1A1A1A] border border-white/10 grid place-items-center text-sm text-gold">
                {u.name.slice(0,1)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm">{u.name}</div>
                <div className="text-xs text-zinc-500">{u.character}</div>
              </div>
              <div className="flex items-center gap-1">
                {STATUSES.map((s) => (
                  <button
                    key={s.v}
                    data-testid={`att-${u.id}-${s.v}`}
                    onClick={() => setStatus(u.id, s.v)}
                    className={`px-2 py-1 rounded border text-xs uppercase tracking-widest transition-all
                      ${cur === s.v ? `${s.color} border-current bg-current/10` : "border-white/10 text-zinc-500 hover:text-white hover:border-white/20"}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
